import _cloneDeep from 'lodash/cloneDeep';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';

import moment from 'moment-timezone';

import BulkDataService from '../api/bulk-data';
import CalendarDataService from '../api/calendar-data';
import CustomersService from '../api/customers';
import EmailCampaignsService from '../api/email-campaign';
import MetricsService from '../api/metrics-customizations';
import ReferralsService from '../api/referrals';
import {
  MAX_DATE_DEFAULT,
  START_DATE_DEFAULT,
} from '../components/historical-metrics/historical-metrics.util';
import { currencyFormat } from '../utils/index';
import StartSession from './StartSession';

export default {
  mixins: [StartSession],
  data() {
    return {
      /* Place all data here */

      /* PK User Info */
      userInfo: {},

      /* PK Payment Info */
      paymentInfo: {},

      /* Calendar */
      // *** BIG DATA ***
      eventsAll: [],
      eventsCancellation: [],
      eventsExpected: [],
      eventsPotential: [],
      eventsDelinquent: [],
      //
      calendarMetrics: {},

      /* Calendar Events */
      calendarEventsData: {},

      /* Live Events */
      liveEventsData: {},

      /* Past Events */
      pastEventsData: {},

      /* MRR Metrics */
      MRRData: {},
      MRRChartData: {},

      // *** NEW METRICS ***
      newMetricsCards: [],
      newMetricsCharts: [],
      newMetricsProjections: {},
      metricsSettings: {},
      metricsCards: [],
      csvString: '',
      breakdownCSVString: '',
      breakdownExcelWorkbook: {},

      // *** Historical Metrics ***
      historicalMetrics: {},

      // exportEmails
      exportEmails: {},

      /* Time Metrics */
      timeData: {},

      /* Customers */
      // *** BIG DATA ***
      customers: [],
      //
      customerMetrics: {},

      /* Recovered Customers */
      // *** BIG DATA ***
      recoveredCustomers: [],

      /* Delinquent Customers */
      // *** BIG DATA ***
      delinquentCustomers: [],

      /* Customers: Expiring Cards */
      expiringCards: [],

      /* Payment Collection Form Data */
      paymentCollectionData: {},
      sampleFormData: {},

      /* Profile Data */
      profileData: {},

      /* Integrations Data */
      integrations: {},

      /* Team Data */
      teamMembers: [],

      /* Referrals Data */
      referrals: {},

      /* Stripe Last Updated Data */
      stripeLastUpdated: '',

      /* Email Campaigns */
      emailCampaigns: [],
      emailCampaignSteps: [],

      loading: {
        login: false,
        calendar: true,
        calendarEvents: true,
        liveEvents: true,
        pastEvents: true,
        revenue: true,
        revenueSettings: true,
        exports: true,
        time: true,
        customers: true,
        historicalMetrics: true,
        recoveredCustomers: true,
        emailCampaigns: true,
        emailCampaignSteps: true,
        delinquentCustomers: true,
        expiringCards: true,
        paymentCollection: true,
        paymentCollectionSnippet: true,
        profile: true,
        integrations: true,
        team: true,
        referrals: true,
        paymentInfo: true, // remove later
        subscriptionProducts: true,
        userInfo: true,
      },

      /* Screen type */
      screen: '' /* stripeConnect, startTrial */,

      /* Refresh timer */
      refreshTimer: null,
    };
  },
  methods: {
    async setGlobalState(key, value) {
      this[key] = value;
    },
    resetLoading(all = true) {
      if (all) {
        const login = this.loading.login;
        this.loading = {
          login: login,
          calendar: true,
          calendarEvents: true,
          liveEvents: true,
          pastEvents: true,
          revenue: true,
          revenueSettings: true,
          exports: true,
          time: true,
          customers: true,
          recoveredCustomers: true,
          delinquentCustomers: true,
          paymentCollection: true,
          profile: true,
          integrations: true,
          team: true,
          referrals: true,
          paymentInfo: true,
          subscriptionProducts: true,
          userInfo: true,
        };
      } else {
        this.loading = {
          login: false,
          calendar: false,
          calendarEvents: false,
          liveEvents: false,
          pastEvents: false,
          revenue: false,
          revenueSettings: false,
          exports: false,
          time: false,
          customers: false,
          recoveredCustomers: false,
          delinquentCustomers: false,
          paymentCollection: false,
          profile: false,
          integrations: false,
          team: false,
          referrals: false,
          paymentInfo: false,
          subscriptionProducts: false,
          userInfo: false,
        };
      }
    },
    async fetchData(
      initialLoad = false,
      showLoader = true,
      specificLoader = '',
      requireCheckAuth = false,
      checkScreens = true,
      recomputed = false,
    ) {
      try {
        if (!showLoader) {
          this.resetLoading(false);
          if (specificLoader && specificLoader !== '') {
            this.loading[specificLoader] = true;
          }
        } else {
          this.resetLoading();
        }

        /* Determine which screen to show */
        await this.setUpScreens(requireCheckAuth, checkScreens);

        const routeAfterLogin = this.$store.getters.getTempVal('routeAfterLogin');

        // Check initial load and if someone came from another route (/payment)
        if (initialLoad && routeAfterLogin !== 'calendar') {
          this.loading.login = false;
          this.$router.push({ name: routeAfterLogin });
          this.$store.dispatch('bulkSetTempState', {
            routeAfterLogin: 'calendar',
          });
        }

        // Calendar Data
        if (!recomputed) {
          BulkDataService.getCalendarData()
            .then(calendarData => {
              this.parseCalendarData(calendarData.data);
              // Initial load with calendar
              if (initialLoad && routeAfterLogin === 'calendar') {
                this.loading.login = false;
                this.$router.push({ name: 'calendar' });
              }
            })
            .catch(err => {
              console.error(err);
            });
        }

        // Time Data
        BulkDataService.getPurchaseTimeData()
          .then(timeData => {
            this.parseTimeData(timeData.data);
          })
          .catch(err => {
            console.error(err);
          });

        // Customers Data
        BulkDataService.getCustomersData()
          .then(customersData => {
            this.parseCustomersData(customersData.data);
          })
          .catch(err => {
            console.error(err);
          });

        /* Load in other data (asynchronous calls)
         * a. 'metricsData'
         * b. 'delinquentCustomersData'
         * c. 'userData'
         * d. 'profileData'
         * e. 'subscriptionData'
         * f. 'referralsData'
         */

        /* A. Metrics Data */
        if (!recomputed) {
          BulkDataService.getMetricsV2Data().then(metricsResultsV2 => {
            this.parseMetricsV2Data(metricsResultsV2.data);
          });
        }

        BulkDataService.getHistoricalMetrics({
          startDate: START_DATE_DEFAULT.format('MMMM YYYY'),
          endDate: MAX_DATE_DEFAULT.format('MMMM YYYY'),
        }).then(historicalMetrics => {
          this.parseHistoricalMetrics(historicalMetrics.data);
        });

        BulkDataService.getMetricsSettings().then(metricsSettings => {
          this.parseMetricsSettings(metricsSettings);
        });

        BulkDataService.getEmailReports().then(emailReports => {
          this.parseExportEmails(emailReports);
        });

        /* B. Delinquent Customers Data */
        CustomersService.getDelinquentCustomers().then(delinquentResults => {
          // Delinquent Customers Data
          this.parseDelinquentCustomersData(delinquentResults.data);
        });

        CustomersService.getRecoveredCustomers().then(recoveredResults => {
          // Recovered Customers Data
          this.parseRecoveredCustomersData(recoveredResults.data);
        });

        CustomersService.getExpiringCards().then(cardResults => {
          this.parseExpiringCards(cardResults.data);
        });

        /* C. User Data */
        BulkDataService.getSimpleData().then(simpleResults => {
          const userData = simpleResults.data;

          this.parseSimpleUserInfo(userData);

          // Stripe Last Updated
          this.parseStripeLastUpdated(userData);

          // Calendar Events Data
          this.parseCalendarEventsData(userData);

          // Live Events Data
          this.parseLiveEventsData(userData);

          // Past Events Data
          this.parsePastEventsData(userData);

          // Payment Collection Data
          this.parsePaymentCollectionData(userData);

          // Integrations Data
          this.parseIntegrationsData(userData);
        });

        /* D. Profile Data */
        BulkDataService.getProfileData().then(profileResults => {
          const profileData = profileResults.data;
          // console.log("ParseData profileData: ", profileData)
          // Profile Data
          this.parseProfileData(profileData);

          // Team Members Data
          this.parseTeamMembers(profileData);
        });

        /* E. Subscription Products + Plans Data */
        BulkDataService.getSubscriptionData()
          .then(subscriptionData => {
            this.parseSubscriptionData(subscriptionData.data);
          })
          .catch(err => {
            console.error(err);
          });

        /* F. Referrals Data */
        ReferralsService.getReferralsInfo().then(referralsData => {
          this.parseReferralsData(referralsData);
        });

        /* G. Email Campaigns Data */
        this.receiveAllEmailCampaigns();
        this.receiveAllCampaingsSteps();
      } catch (err) {
        console.error('Error fetching data', err);
        return false;
      }
    },
    async refetchStripeData(showLoader = false, force = false) {
      // If timer not null, refetch already in progress, don't want to call again
      if (this.refreshTimer === null) {
        const stripeData = await this.checkRefetchedData({
          firstCheck: true,
          force,
        });
        // If data is loading, try again after 20 seconds
        if (stripeData.data_loading || stripeData.error) {
          this.refreshTimer = setInterval(() => {
            this.checkRefetchedData({
              firstCheck: false,
              showLoader,
              force,
            });
          }, 20000);
        }
      }
    },
    async checkRefetchedData(params) {
      const { firstCheck, showLoader, force } = params;
      const results = await BulkDataService.getRefetchedStripeData(firstCheck, force);
      const stripeData = results.data;
      // If data is not loading, finish the call
      if (stripeData.terminate || !stripeData.data_loading) {
        /* Clear screen */
        this.screen = '';

        /* Clear timer */
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;

        /* Fetch data, with auth/me refetch */
        if (showLoader) {
          this.fetchData(false, true, '', true, false);
        } else {
          this.fetchData(false, false, '', true, false);
        }
      }
      return stripeData;
    },
    async setUpScreens(requireCheckAuth = false, checkScreens = true) {
      BulkDataService.getPaymentData().then(response => {
        this.parsePaymentInfo(response.data, checkScreens);
      });

      // ***
      const authUserData = requireCheckAuth
        ? (await BulkDataService.getUserData()).data
        : this.userInfo;
      // ****
      if (requireCheckAuth) {
        this.parseUserInfo(authUserData);
      }

      const hasUserInfo = !_isEmpty(authUserData);

      if (checkScreens) {
        /* Check screens */
        if (hasUserInfo && !authUserData.stripeConnected) {
          /* Stripe Connect */
          this.screen = 'stripeConnect';
        } else if (
          hasUserInfo &&
          authUserData.stripeConnected &&
          !authUserData.loaded_stripe_data
        ) {
          /* Stripe Downloading */
          this.screen = 'stripeDownloading';
        }
      }
    },
    setUpLoader(key) {
      this.loading[key] = true;
    },
    destroyTimer() {
      if (this.refreshTimer !== null) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    parseCalendarData(stripeData) {
      if (!_isEmpty(stripeData)) {
        // Calendar Data
        const isFuture = this.$store.getters.getVal('calendarPeriod') === 'Future';
        this.eventsAll = isFuture ? stripeData.events : stripeData.ALL;
        this.eventsCancellation = isFuture
          ? stripeData.events_cancellation
          : stripeData.CANCELLATION;
        this.eventsExpected = isFuture ? stripeData.events_expected : stripeData.SUCCESSFUL_PAYMENT;
        this.eventsPotential = isFuture ? stripeData.events_potential : [];
        this.eventsDelinquent = isFuture
          ? stripeData.events_delinquent
          : stripeData.OVERDUE_PAYMENT;

        if (stripeData.intervalData) {
          this.calendarMetrics = stripeData.intervalData;
        }
      }
      this.loading.calendar = false;
    },
    parseCalendarEventsData(userData) {
      this.calendarEventsData = {
        // additionalEmailTriggers: userData.additional_email_triggers,
        // emails: userData.calendar_event_emails,
        emails: userData.calendar_event_emails_getter,
        notifications: userData.notifications,
      };
      this.$store
        .dispatch(
          'bulkSetState',
          _cloneDeep({
            calendarEventEmails: userData.calendar_event_emails_getter,
            calendarEventNotifications: userData.notifications,
          }),
        )
        .then(() => {
          this.loading.calendarEvents = false;
        })
        .catch(err => {
          console.error(err);
          this.loading.calendarEvents = false;
        });
    },
    parseLiveEventsData(userData) {
      // Payment Collection Emails / Webhook Settings
      this.liveEventsData = {
        expiredTrialEmails: userData.live_expired_trials_emails_getter,
        pastDueEmails: userData.past_due_emails_getter,
        unpaidEmails: userData.unpaid_emails_getter,
        successfulUpdates: userData.payment_collection_form.successful_updates_count,
      };

      const strippedLiveEventsData = {
        expiredTrialEmails: { ...userData.live_expired_trials_emails_getter, sentEmails: [] },
        pastDueEmails: { ...userData.past_due_emails_getter, sentEmails: [] },
        unpaidEmails: { ...userData.unpaid_emails_getter, sentEmails: [] },
        successfulUpdates: userData.payment_collection_form.successful_updates_count,
      };

      this.$store
        .dispatch('bulkSetState', {
          webhookSettings: strippedLiveEventsData,
          webhookSettingsCopy: _cloneDeep(strippedLiveEventsData),
        })
        .then(() => {
          this.loading.liveEvents = false;
        })
        .catch(err => {
          this.loading.liveEvents = false;
        });
    },
    parsePastEventsData(userData) {
      this.pastEventsData = {
        emails: userData.past_event_emails_getter,
        notifications: userData.past_event_notifications,
      };

      this.$store
        .dispatch(
          'bulkSetState',
          _cloneDeep({
            pastEventEmails: userData.past_event_emails_getter,
            pastEventNotifications: userData.past_event_notifications,
          }),
        )
        .then(() => {
          this.loading.pastEvents = false;
        })
        .catch(err => {
          console.error(err);
          this.loading.pastEvents = false;
        });
    },
    async fetchRecomputedCalendarData(showLoader = true) {
      if (showLoader) {
        this.loading.calendar = true;
      }
      await CalendarDataService.getRecomputedCalendar({}); // Returns V1 calendar format
      const response = await BulkDataService.getCalendarData();
      this.parseCalendarData(response.data);
      this.loading.calendar = false;
    },
    async fetchAllRecomputedCalendarData(showLoader = true) {
      // todo is it useful method?
      if (showLoader) {
        this.loading.calendar = true;
      }

      await Promise.all([
        CalendarDataService.getRecomputedCalendar({}),
        CalendarDataService.getRecomputedCalendar({ historical: true }),
      ]);

      const response = await BulkDataService.getCalendarData();
      this.parseCalendarData(response.data);

      this.loading.calendar = false;
    },
    async fetchRecomputedMetricsData(showLoader = true, payload = {}) {
      if (showLoader) {
        this.loading.revenue = true;
      }
      const response = await MetricsService.getRecomputedMetrics(payload);
      this.parseMetricsV2Data(response.data);
      this.loading.revenue = false;
    },
    async fetchAllRecomputedData(showLoader, metricsPayload = {}, getAllData) {
      if (getAllData) {
        this.resetLoading(true);
      }

      await Promise.all([
        this.fetchRecomputedCalendarData(showLoader),
        this.fetchRecomputedMetricsData(showLoader, metricsPayload),
      ]);

      if (getAllData) {
        this.fetchData(
          false, // initialLoad
          true, // showLoader
          '', // specificLoader
          false, // requiredCheckAuth
          true, // checkScreens
          true, // recomputed
        );
      }

      this.loading.calendar = false;
      this.loading.revenue = false;
    },
    async teamAllRecomputedData(showLoader, metricsPayload = {}, getAllData) {
      this.loading.login = true;

      if (getAllData) {
        this.resetLoading(true);
      }

      this.loading.login = false;
      this.$router.push({ name: 'calendar' });

      await Promise.all([
        this.fetchRecomputedCalendarData(showLoader),
        this.fetchRecomputedMetricsData(showLoader, metricsPayload),
      ]);

      if (getAllData) {
        this.fetchData(
          false, // initialLoad
          true, // showLoader
          '', // specificLoader
          true, // requiredCheckAuth -- different
          true, // checkScreens
          true, // recomputed
        );
      }

      this.loading.calendar = false;
      this.loading.revenue = false;
    },
    parseMetricsV2Data(metricsData) {
      this.newMetricsCards = metricsData.metricsCards;
      this.newMetricsCharts = metricsData.metricsCharts;
      this.newMetricsProjections = metricsData.projectionValues;
      this.$store.commit('expandAllMetrics', {
        allMetrics: this.newMetricsCards,
        shouldExpand: false,
      });
      this.loading.revenue = false;
    },
    parseHistoricalMetrics(historicalMetrics) {
      this.historicalMetrics = historicalMetrics;
      this.loading.historicalMetrics = false;
    },
    parseEmailCampaigns(emailCampaigns) {
      this.emailCampaigns = emailCampaigns;
      this.loading.emailCampaigns = false;
    },
    parseEmailCampaignSteps(emailCampaignSteps) {
      this.emailCampaignSteps = emailCampaignSteps;
      this.loading.emailCampaignSteps = false;
    },
    parseMetricsSettings(metricsData) {
      this.metricsSettings = {
        settings: metricsData[0].data,
        filter: metricsData[1].data,
        excludedCustomers: metricsData[1].data.excluded_customers,
        overriddenCustomers: metricsData[1].data.active_override_customers,
      };
      this.loading.revenueSettings = false;
    },
    parseSpecificMetricsSettings(key, value) {
      this.metricsSettings[key] = value;
    },
    parseMetricsUpdate(metricsData, payload) {
      const { key, value } = payload;
      this.parseMetricsV2Data(metricsData);
      this.parseSpecificMetricsSettings(key, value);
    },
    parseExportEmails(emailReports) {
      this.exportEmails = emailReports.data;
      this.loading.exports = false;
    },
    parseTimeData(stripeData) {
      if (!_isEmpty(stripeData)) {
        // TO DO -- remove this check when the refetched Stripe data also has these fields
        if (stripeData.PurchaseTimeData) {
          this.timeData = stripeData.PurchaseTimeData.subscriptions;
        }
      }
      this.loading.time = false;
    },
    formatPrice(value) {
      return currencyFormat(value, {
        currencySymbol: this.$store.getters.getVal('currencySymbol'),
        decimals: 2,
      });
    },
    formatDate(value) {
      return moment(value).format('MMMM D, YYYY');
    },
    parseCustomersData(stripeData) {
      const isLargeAccount = this.$store.getters.isLargeAccount;

      if (!isLargeAccount) {
        if (!_isEmpty(stripeData)) {
          this.customers = stripeData.customers.map(customer => {
            if (!customer.email) {
              // eslint-disable-next-line
              customer.email = customer.id;
            }

            return customer;
          });
        }
      }

      this.loading.customers = false;
    },
    parseExpiringCards(cardData) {
      this.expiringCards = cardData;
      this.loading.expiringCards = false;
    },
    parseRecoveredCustomersData(userData) {
      this.recoveredCustomers = userData.recovered_tracker.map(customer => {
        if (!customer.email) {
          // eslint-disable-next-line
          customer.email = customer.customer_id;
        }

        // eslint-disable-next-line
        customer.email_sent_date_formatted = customer.email_sent_date
          ? this.formatDate(customer.email_sent_date)
          : '\u2014';

        // eslint-disable-next-line
        customer.date_recovered_formatted = customer.date_recovered
          ? this.formatDate(customer.date_recovered)
          : '\u2014';

        /* Format Recovered MRR */
        // eslint-disable-next-line
        customer.revenue_recovered_formatted = this.formatPrice(customer.revenue_recovered);

        // eslint-disable-next-line
        customer.MRR_recovered_formatted = `${this.formatPrice(customer.MRR_recovered)} / month`;

        return customer;
      });

      this.loading.recoveredCustomers = false;
    },
    parseDelinquentCustomersData(delinquentCustomers) {
      this.delinquentCustomers = delinquentCustomers.map(customer => {
        if (_isArray(customer.email) && customer.email.length > 0) {
          // eslint-disable-next-line
          customer.email = customer.email[0];
        }

        if (!customer.email) {
          // eslint-disable-next-line
          customer.email = customer.customer_id;
        }

        // eslint-disable-next-line
        customer.total_MRR_formatted = this.formatPrice(customer.total_MRR);

        const hasEmailSentDate = customer.email_sent && customer.email_sent_date;

        // eslint-disable-next-line
        customer.email_sent_date_filter = hasEmailSentDate
          ? moment(customer.email_sent_date).unix()
          : 0;

        // eslint-disable-next-line
        customer.email_sent_date_formatted = hasEmailSentDate
          ? this.formatDate(customer.email_sent_date)
          : '';

        return customer;
      });

      this.loading.delinquentCustomers = false;
    },
    parsePaymentCollectionData(userData) {
      this.paymentCollectionData = userData.payment_collection_form;
      this.sampleFormData = {
        // get part after 'update-payment'
        sampleLinkFull: userData.sample_payment_link,
        sampleLink: `${userData.sample_payment_link.split('update-payment/')[1]}`,
        samplePlan: userData.sample_plan_name,
        sampleProduct: userData.sample_product_name,
      };
      this.loading.paymentCollection = false;
    },
    getSamplePaymentLink(callback) {
      const samplePaymentLinkResponse = BulkDataService.getSamplePaymentLink()
        .then(response => {
          callback(response.data.sample_payment_link);
          return response.data.sample_payment_link;
        })
        .catch(err => {
          console.error(err);
          const sampleLinkChunks = this.sampleFormData.sampleLinkFull;
          callback(sampleLinkChunks);
          return sampleLinkChunks;
        });
    },
    /* Supplementary Pages */
    parseProfileData(profileData) {
      this.profileData = profileData;
      this.$store
        .dispatch('bulkSetState', {
          accountName: profileData.display_name,
          timezone: profileData.timezone,
          currencySymbol: profileData.currency_symbol,
        })
        .then(() => {
          this.loading.profile = false;
        })
        .catch(err => {
          console.error('Error Parsing Profile Data: ', err);
          this.loading.profile = false;
        });
    },
    parseIntegrationsData(userData) {
      this.integrations = {
        emailIntegrations: userData.email_integrations,
        selectedEmailIntegration: userData.selected_email_integration,
      };

      this.$store
        .dispatch('bulkSetState', {
          emailIntegrationInfo: userData.email_integrations,
          emailIntegration: userData.selected_email_integration,
        })
        .then(() => {
          this.loading.integrations = false;
        })
        .catch(err => {
          console.error(err);
          this.loading.integrations = false;
        });
    },
    parseSubscriptionData(subscriptionData) {
      this.$store.commit('setState', {
        path: 'productsAndPlans',
        value: subscriptionData.productPlanArray,
      });

      this.loading.subscriptionProducts = false;
    },
    parseReferralsData(referralsData) {
      this.referrals = {
        referralCount: referralsData.data.referral_count,
        referralLink: referralsData.data.referral_link,
        trialEnd: referralsData.data.trial_end_formatted,
      };

      this.loading.referrals = false;
    },
    parseTeamMembers(profileData) {
      this.teamMembers = profileData.team_members;

      this.loading.team = false;
    },
    parseStripeLastUpdated(userData) {
      if (this.stripeLastUpdated === userData.stripe_last_updated) {
        // Reset stripeLastUpdated to trigger Vue's reactivity
        this.stripeLastUpdated = '';
        const stripeUpdateTimer = setTimeout(() => {
          this.stripeLastUpdated = userData.stripe_last_updated;
          clearTimeout(stripeUpdateTimer);
        }, 3000);
      } else {
        this.stripeLastUpdated = userData.stripe_last_updated;
      }
    },
    parsePaymentInfo(authPaymentData, checkScreens = true) {
      this.paymentInfo = authPaymentData;
      const authUserData = this.userInfo;
      const hasUserInfo = !_isEmpty(authUserData);
      this.loading.paymentInfo = false;

      if (checkScreens) {
        /* Check screens */
        if (hasUserInfo && !authUserData.stripeConnected) {
          /* Stripe Connect */
          this.screen = 'stripeConnect';
        } else if (
          hasUserInfo &&
          authUserData.stripeConnected &&
          !authUserData.loaded_stripe_data
        ) {
          /* Stripe Downloading */
          this.screen = 'stripeDownloading';
        } else if (
          !authPaymentData.has_existing_subscription &&
          !authPaymentData.subscription_valid
        ) {
          /* Start Trial */
          this.screen = 'startTrial';
        } else if (
          authPaymentData.has_existing_subscription &&
          !authPaymentData.subscription_valid
        ) {
          /* Purchase Subscription */
          this.screen = 'purchaseSubscription';
        } else {
          /* General */
          this.screen = '';
        }
      } else {
        if (!authPaymentData.has_existing_subscription && !authPaymentData.subscription_valid) {
          /* Start Trial */
          this.screen = 'startTrial';
        } else if (
          authPaymentData.has_existing_subscription &&
          !authPaymentData.subscription_valid
        ) {
          /* Purchase Subscription */
          this.screen = 'purchaseSubscription';
        } else {
          /* General */
          this.screen = '';
        }
      }
    },
    clearState(variable, newVal) {
      this[variable] = newVal;
    },
    parseSimpleUserInfo(userData) {
      this.$store
        .dispatch('bulkSetState', {
          userInfo: {
            user: _pick(userData, ['id', 'email', 'name']),
            is_team_owner: userData.is_team_owner,
          },
        })
        .then(() => {
          this.onAuth(); // Launch Intercom with auth configuration
        })
        .catch(err => {
          console.error('There was an error fetching your user information:', err);
        });
    },
    async parseUserInfo(authUserData) {
      this.userInfo = { ...authUserData, user: {} }; // remove unnecessary fields
      try {
        await this.$store.dispatch('bulkSetState', {
          readOnlyAccess: authUserData.user.read_only_access,
          read_only_connect_routes: authUserData.read_only_connect,
          read_write_connect_routes: authUserData.read_write_connect,
          selected_email_integration: authUserData.user.selected_email_integration,
          google_api_credentials: authUserData.user.google_api_credentials,
          metadata: authUserData.user.user_metadata,
        });
      } catch (err) {
        console.error('Error setting user info in store:', err);
      } finally {
        this.loading.userInfo = false;
        this.loading.paymentCollectionSnippet = false;
      }
    },
    receiveAllEmailCampaigns() {
      EmailCampaignsService.getAllEmailCampaigns().then(emailCampaigns => {
        this.parseEmailCampaigns(emailCampaigns.data);
      });
    },
    receiveAllCampaingsSteps() {
      EmailCampaignsService.getAllEmailCampaignSteps()
        .then(emailCampaignSteps => {
          this.parseEmailCampaignSteps(emailCampaignSteps.data);
        })
        .catch(error => {
          console.error('Error fetching email campaign steps:', error);
          this.loading.emailCampaignSteps = false;
        });
    },
  },
  watch: {
    screen: {
      handler: function(newVal, oldVal) {
        this.$store.commit('setTempState', {
          path: 'screen',
          value: newVal,
        });

        if (newVal !== oldVal && newVal === 'stripeDownloading') {
          this.refetchStripeData(true); // showLoader = true
        }
      },
    },
  },
};
